// Globalny axios interceptor: dokleja Bearer + X-Org-Slug do wszystkich
// wywolan osiowych w aplikacji. Instalowany raz, z boot (index.tsx).
//
// Token: admin Supabase session.access_token lub operator JWT z localStorage.
// Slug: REACT_APP_DEFAULT_ORG_SLUG (fallback) lub z subdomeny (runtime).
import axios from 'axios';
import { API_URL } from './api';
import { supabase } from './supabase';

const OPERATOR_TOKEN_KEY = 'packflow.operator_token';

// Slug z hosta TYLKO dla subdomen tenantów (<slug>.APP_BASE_DOMAIN) — etykieta
// z obcych domen (np. packflow-eight.vercel.app) nie jest slugiem organizacji.
const APP_BASE_DOMAIN = (process.env.REACT_APP_BASE_DOMAIN || 'packflow.insolitum.ai').toLowerCase();

function currentSlug(): string | null {
  const host = window.location.hostname.toLowerCase();
  const suffix = '.' + APP_BASE_DOMAIN;
  if (host.endsWith(suffix)) {
    const first = host.slice(0, -suffix.length).split('.')[0];
    if (first && !['www', 'app', 'api'].includes(first) && /^[a-z0-9-]{3,40}$/.test(first)) {
      return first;
    }
  }
  // Tryb kiosk / apex: slug wstrzyknięty przez aplikację PackFlow Station (preload)
  // lub przekazany w ?org=<slug> — pozwala rozwiązać tenanta bez subdomeny.
  const stored = localStorage.getItem('packflow.org_slug');
  if (stored && /^[a-z0-9-]{3,40}$/.test(stored)) return stored;
  return process.env.REACT_APP_DEFAULT_ORG_SLUG || null;
}

// ?org=<slug> (kiosk otwiera apex z tym parametrem) -> zapamiętaj na potrzeby X-Org-Slug.
function captureOrgFromUrl(): void {
  try {
    const q = new URLSearchParams(window.location.search).get('org');
    if (q && /^[a-z0-9-]{3,40}$/.test(q)) localStorage.setItem('packflow.org_slug', q);
  } catch { /* brak window/search */ }
}

async function currentToken(): Promise<string | null> {
  if (supabase) {
    try {
      const { data } = await supabase.auth.getSession();
      if (data.session?.access_token) return data.session.access_token;
    } catch { /* fall through to operator token */ }
  }
  return localStorage.getItem(OPERATOR_TOKEN_KEY);
}

let installed = false;
export function installAxiosInterceptors() {
  if (installed) return;
  installed = true;

  captureOrgFromUrl();

  // baseURL: jesli caller podaje absolutny URL - axios go uzyje; wpp dokleja API_URL.
  axios.defaults.baseURL = axios.defaults.baseURL || API_URL;

  axios.interceptors.request.use(async (config) => {
    const token = await currentToken();
    if (token) {
      config.headers = config.headers || {};
      (config.headers as any).Authorization = `Bearer ${token}`;
    }
    const slug = currentSlug();
    if (slug) {
      config.headers = config.headers || {};
      (config.headers as any)['X-Org-Slug'] = slug;
    }
    return config;
  });

  // 401 -> globalny event, nad ktorym moze nasluchiwac AuthProvider i czyscic sesje.
  // Dodatkowo: API v2 zwraca jeden ksztalt bledu {error:{code,message}} —
  // normalizujemy go do stringa, zeby istniejace `e?.response?.data?.error`
  // w calym kliencie dalej pokazywaly czytelny komunikat.
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      if (err?.response?.status === 401) {
        window.dispatchEvent(new CustomEvent('packflow:auth-expired'));
      }
      const e = err?.response?.data?.error;
      if (e && typeof e === 'object' && typeof e.message === 'string') {
        err.response.data.error = e.message;
        err.response.data.errorCode = e.code;
      }
      return Promise.reject(err);
    }
  );
}
